import {
  Component,
  computed,
  effect,
  inject,
  Input,
  input,
  output,
  signal,
  ChangeDetectionStrategy
} from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import { FormBuilder, FormControl, Validators } from "@angular/forms";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { FormsAPIService } from "../forms-api.service";
import { DescriptiveFormTemplate, FormTemplate } from "../forms.model";

@Component({
  selector: "app-form-loader",
  templateUrl: "./form-loader.component.html",
  styleUrls: ["./form-loader.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FormLoaderComponent {
  private formsAPIService = inject(FormsAPIService);
  private fb = inject(FormBuilder);

  readonly showOnlyActive = input<boolean>(false);
  readonly loadOnSelectionChange = input<boolean>(false);
  readonly appearance = input<"outline" | "fill">("fill");

  readonly formSelected = output<FormTemplate>();
  readonly emitLoading = output<boolean>();

  selectForm = this.fb.group({
    selectedForm: new FormControl<string | null>(null, [Validators.required]),
  });

  readonly templatesResource = rxResource({
    params: () => this.showOnlyActive(),
    stream: ({ params: active }) => {
      return this.formsAPIService.getAllDescriptive().pipe(
        map((data) => {
          if (active) {
            data = data.filter((t) => t.active);
          }
          return data.sort((a, b) => (a.title < b.title ? -1 : 1));
        }),
      );
    },
  });

  readonly loadedTemplates = this.templatesResource.value;
  uuid = signal<string | null>(null);

  readonly formsResource = rxResource({
    params: () => this.uuid(),
    stream: ({ params: currentUuid }) => {
      if (!currentUuid) {
        return of(null);
      }
      return this.formsAPIService.findByUuid(currentUuid);
    },
  });

  readonly form = this.formsResource.value;
  readonly isLoading = this.formsResource.isLoading;
  readonly hasError = computed(() => this.formsResource.error() !== undefined);
  readonly formLoaded = computed(() => !!this.form());

  constructor() {
    effect(() => {
      this.emitLoading.emit(this.isLoading());

      const loadedForm = this.form();
      if (!this.isLoading() && loadedForm) {
        this.formSelected.emit(loadedForm);
      }
    });
    effect(() => {
      if (this.loadOnSelectionChange()) {
        return;
      }
      if (this.formLoaded()) {
        this.selectForm.controls.selectedForm.disable({ emitEvent: false });
      } else {
        this.selectForm.controls.selectedForm.enable({ emitEvent: false });
      }
    });
  }

  loadForm() {
    const selectedUuid = this.selectForm.controls.selectedForm.value;
    if (selectedUuid) {
      this.uuid.set(selectedUuid);
    }
  }

  clearRefresh() {
    this.selectForm.controls.selectedForm.setValue(null, { emitEvent: false });
    this.uuid.set(null);
    this.formSelected.emit(null);
  }

  formSelectionChange(uuid: string) {
    if (!this.loadOnSelectionChange() || !uuid) {
      return;
    }
    this.uuid.set(uuid);
  }
}
